import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface PaymentWallProps {
  pageName: string;
  children: React.ReactNode;
}

export const PaymentWall = ({ pageName, children }: PaymentWallProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [priceIds, setPriceIds] = useState<{
    monthly: string | null;
    yearly: string | null;
  }>({
    monthly: null,
    yearly: null,
  });

  useEffect(() => {
    const fetchPriceIds = async () => {
      const { data: monthlyData, error: monthlyError } = await supabase
        .functions.invoke('get-env', { body: { key: 'STRIPE_PROD_ID_MONTHLY' } });

      const { data: yearlyData, error: yearlyError } = await supabase
        .functions.invoke('get-env', { body: { key: 'STRIPE_PROD_ID_YEARLYDISCOUNTED' } });

      if (monthlyError || yearlyError) {
        console.error('Error fetching price IDs:', { monthlyError, yearlyError });
        return;
      }

      setPriceIds({
        monthly: monthlyData?.value || null,
        yearly: yearlyData?.value || null,
      });
    };

    fetchPriceIds();
  }, []);

  const handleCheckout = async (priceId: string) => {
    setIsLoading(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        toast({
          title: "Authentication Required",
          description: "Please log in to continue with your purchase",
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      const response = await supabase.functions.invoke('create-checkout-session', {
        body: { 
          priceId,
          userId: session.user.id,
          returnUrl: window.location.origin + '/opportunities'
        },
      });

      if (response.error) {
        if (response.error.message.includes('User already has an active subscription')) {
          toast({
            title: "Already Subscribed",
            description: "You already have an active subscription. Redirecting...",
          });
          navigate('/opportunities');
          return;
        }
        
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Checkout Error",
        description: error instanceof Error ? error.message : "Failed to start checkout process",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 backdrop-blur-sm z-10" />
      <div className="relative z-20">
        {children}
      </div>
      <div className="container max-w-2xl mx-auto px-4 py-8 relative z-30">
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium">Premium Feature</span>
            </div>
            <CardTitle className="text-2xl">Upgrade to Access {pageName}</CardTitle>
            <CardDescription>
              Get unlimited access to all features and opportunities
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {priceIds.monthly && (
                <Button
                  className="w-full h-auto py-4 px-6"
                  onClick={() => handleCheckout(priceIds.monthly!)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Monthly Plan
                </Button>
              )}
              {priceIds.yearly && (
                <Button
                  className="w-full h-auto py-4 px-6"
                  onClick={() => handleCheckout(priceIds.yearly!)}
                  disabled={isLoading}
                  variant="secondary"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Yearly Plan
                  <span className="ml-2 text-xs bg-primary/10 px-2 py-1 rounded-full">Save 20%</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};